import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import CloseIcon from "assets/icons/close.svg?react";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import NotesService from "api/NotesService";
import { INote } from "components/NotesDesk";
import BinIcon from "assets/icons/bin.svg?react";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import { decryptPackage } from "utils/decryptPackage";
import decryptNoteFields from "utils/decryptNoteFields";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

const decryptNoteByInter = async (userId: string, msg: string) => {
  return await decryptPackage(userId!, msg);
};

const NotiBox = ({
  note,
  onClick,
  handleDelete,
  notes,
  setNotes,
}: {
  note: INote;
  onClick: (note: INote, title: string, content: string) => void;
  handleDelete: (note: INote) => void;
  notes: INote[];
  setNotes: (notes: INote[]) => void;
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const userId = getUserId(auth.user!);

  const [hover, setHover] = useState(false);

  const notiBoxRef = useRef(null);

  const [startedDecode, setStartedDecode] = useState(false);

  const handleDecode = async () => {
    setStartedDecode(true);
    if (!note.decoded) {
      const copy = [...notes];

      const element = copy.find((a) => a.Id === note.Id);
      if (!element) return;

      if (note.EncryptionKeyId) {
        // Notatka zapisana po F3: treść szyfruje klucz OBIEKTU, owinięty kluczem FIRMY
        // (utils/encryptNoteFields). decryptPackage rozpakowuje tylko treść szyfrowaną
        // wprost kluczem kontekstu, więc tu idzie dwustopniowa ścieżka obiektowa.
        //
        // Gałąź `else` obsługuje wiersze SPRZED migracji EncryptionKeyId. Purge F4 NIE
        // został uruchomiony na produkcji, więc one wciąż istnieją — komponenty NotesDesk
        // i modules/deskModule trzymają się tej samej zasady (wcześniej zakładały, że purge
        // przeszedł, i każda stara notatka kończyła się tam błędem „Brak klucza").
        const fields = await decryptNoteFields(note, []);
        element.Title = fields.title;
        element.Content = fields.content;
      } else {
        element.Title = await decryptNoteByInter(userId, note.Title);
        element.Content = await decryptNoteByInter(userId, note.Content);
      }
      element.decoded = true;

      setNotes(copy);
    }
  };

  useEffect(() => {
    // setIsVisible(true);
    if (startedDecode) return;
    if (note.decoded) return;
    handleDecode();
  }, [note]);

  return (
    <div
      ref={notiBoxRef}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => {
        onClick(note, note.Title, note.Content);
      }}
      className={styles.box}
    >
      {hover && (
        <Tooltip title={t("common.delete")}>
          <div>
            <BinIcon
              onClick={(e) => {
                handleDelete(note);
                e.preventDefault();
                e.stopPropagation();
              }}
              className={styles.remove}
            />
          </div>
        </Tooltip>
      )}
      <div className={styles.wrapBox}>
        <div className={styles.data}>
          <div className={styles.title}>
            <div
              dangerouslySetInnerHTML={{
                __html: note.decoded ? note.Title : t("common.loadingShort"),
              }}
              className={styles.left}
            ></div>
            <div className={styles.right}>
              {moment(note.CreationDate).format("DD.MM.YY")}
            </div>
          </div>
          <div
            dangerouslySetInnerHTML={{
              __html: note.decoded ? note.Content : t("common.loadingShort"),
            }}
            className={styles.description}
          ></div>
        </div>
      </div>
    </div>
  );
};

const NotesModal: FC<Props> = ({ toggle, invoiceId, apiRef }) => {
  const { t } = useTranslation();
  const { data: notesData, refetch } = NotesService.useGetNotes(
    null,
    null,
    invoiceId,
  );
  const { mutate: deleteNote } = NotesService.useDeleteNote();

  const [notes, setNotes] = useState<INote[]>([]);

  const handleDeleteNote = (note: INote) => {
    deleteNote(
      {
        Id: note.Id,
      },
      {
        onSuccess() {
          apiRef.current.updateRows([
            { id: invoiceId, NotesCount: notes.length - 1 },
          ]);
          if (notes.length === 1) {
            toggle(false);
          }
          refetch();
        },
      },
    );
  };

  useEffect(() => {
    if (notesData?.Items) {
      setNotes(notesData.Items);
    }
  }, [notesData]);

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <div className={styles.title}>
            {t("notes.title")}
            <CloseIcon
              className={styles.closeIcon}
              onClick={(e) => {
                toggle(false);
                e.stopPropagation();
              }}
            />
          </div>
          <div className={styles.list}>
            {notes.map((n) => (
              <NotiBox
                note={n}
                handleDelete={handleDeleteNote}
                onClick={() => {}}
                notes={notes}
                setNotes={setNotes}
              ></NotiBox>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default NotesModal;
